import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Mail, Building2, Phone, User } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DEFAULT_STEPS = [
  { key: 'queued', label: 'Uploaded, queued for extraction' },
  { key: 'extracting', label: 'Extracting bill details' },
  { key: 'analyzing', label: 'Sizing solar + battery' },
  { key: 'ready', label: 'Computing annual savings' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Dial codes UrjaPod actually sees enquiries from, India first. `digits` is the
// length of the national number, which is why the rule cannot simply be "10" for
// everyone — a UAE mobile is 9 and a Singapore one is 8.
const COUNTRIES = [
  { iso: 'IN', name: 'India', dial: '+91', digits: 10 },
  { iso: 'AE', name: 'United Arab Emirates', dial: '+971', digits: 9 },
  { iso: 'SA', name: 'Saudi Arabia', dial: '+966', digits: 9 },
  { iso: 'OM', name: 'Oman', dial: '+968', digits: 8 },
  { iso: 'QA', name: 'Qatar', dial: '+974', digits: 8 },
  { iso: 'SG', name: 'Singapore', dial: '+65', digits: 8 },
  { iso: 'LK', name: 'Sri Lanka', dial: '+94', digits: 9 },
  { iso: 'NP', name: 'Nepal', dial: '+977', digits: 10 },
  { iso: 'BD', name: 'Bangladesh', dial: '+880', digits: 10 },
  { iso: 'GB', name: 'United Kingdom', dial: '+44', digits: 10 },
  { iso: 'US', name: 'United States', dial: '+1', digits: 10 },
  { iso: 'AU', name: 'Australia', dial: '+61', digits: 9 },
];

const DEFAULT_COUNTRY = COUNTRIES[0];

/** Digits only, trimmed to what the selected country expects.
 *
 * People paste "+91 98765 43210" or "098765 43210" out of a contact card. Naively
 * stripping and truncating would keep "9198765432" — a wrong number that looks
 * right — so the dial code and the trunk zero are dropped first.
 */
function toNationalDigits(raw, country) {
  const text = String(raw).trim();
  const dial = country.dial.replace('+', '');
  let digits = text.replace(/\D/g, '');

  // A leading "+" says the country code is included, so drop it even when the
  // result is short. Checking the length alone is not enough: typed one key at a
  // time, the value is capped at 10 before it ever exceeds 10, and "+91 98765
  // 43210" would silently settle as "9198765432" — a plausible-looking wrong
  // number.
  if (text.startsWith('+') && digits.startsWith(dial)) {
    digits = digits.slice(dial.length);
  } else if (digits.length > country.digits && digits.startsWith(dial)) {
    digits = digits.slice(dial.length);
  }

  // Trunk prefix from a pasted local format, e.g. "098765 43210".
  if (digits.length > country.digits && digits.startsWith('0')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, country.digits);
}

const PROGRESS_MOTION_INITIAL = { opacity: 0, y: 8 };
const PROGRESS_MOTION_ANIMATE = { opacity: 1, y: 0 };
const FORM_MOTION_TRANSITION = { delay: 0.05 };

function stepIndex(status) {
  return DEFAULT_STEPS.findIndex((s) => s.key === status);
}

function friendlyErrorMessage(error) {
  if (!error) return '';
  const raw = String(error);
  if (raw.toLowerCase().includes('budget')) {
    return 'The AI extraction service is temporarily unavailable (usage budget exceeded). Please try again in a bit, or contact the UrjaPod team.';
  }
  return raw;
}

export const ProcessingState = ({ status, onSubmitCustomer, submitted, error }) => {
  const [personName, setPersonName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY.iso);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  const idx = Math.max(0, stepIndex(status?.status));
  const progress = Math.min(100, Math.max(0, Number(status?.progress || (idx + 1) * 25)));

  const selectedCountry = COUNTRIES.find((c) => c.iso === country) || DEFAULT_COUNTRY;
  const emailValid = EMAIL_RE.test(email);
  // Optional field: blank is fine, half a number is not.
  const phoneValid = phone.length === 0 || phone.length === selectedCountry.digits;
  const canSubmit =
    personName.trim().length > 1 &&
    company.trim().length > 1 &&
    emailValid &&
    phoneValid &&
    !busy &&
    !submitted;

  const changeCountry = (iso) => {
    setCountry(iso);
    // Re-trim: switching India (10) to Oman (8) must not leave 10 digits behind.
    const next = COUNTRIES.find((c) => c.iso === iso) || DEFAULT_COUNTRY;
    setPhone((current) => current.slice(0, next.digits));
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onSubmitCustomer({
        person_name: personName.trim(),
        company_name: company.trim(),
        email: email.trim(),
        // Sent in E.164 so it can be dialled or used for WhatsApp as-is.
        contact_number: phone ? `${selectedCountry.dial}${phone}` : undefined,
      });
      toast.success('Details saved. We\u2019ll send you the analysis and follow up.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save details');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="processing-screen">
      <div className="grid gap-6 lg:grid-cols-12">
        {/* Progress + Checklist */}
        <motion.div
          initial={PROGRESS_MOTION_INITIAL}
          animate={PROGRESS_MOTION_ANIMATE}
          className="lg:col-span-7"
        >
          <Card className="rounded-2xl border-border/70 bg-card p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Analyzing your bill
              </h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              This usually takes 10–30 seconds. You can fill your details on the right while we run
              the analysis.
            </p>

            <div className="mt-6" data-testid="analysis-progress">
              <Progress value={progress} className="h-2" />
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="progress-current-step">{status?.step || 'Preparing…'}</span>
                <span className="tabular">{Math.round(progress)}%</span>
              </div>
            </div>

            <ul className="mt-6 space-y-3" data-testid="processing-checklist">
              {DEFAULT_STEPS.map((s, i) => {
                const done = i < idx || status?.status === 'ready';
                const active = i === idx && status?.status !== 'ready';
                return (
                  <li key={s.key} className="flex items-center gap-3">
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
                    ) : active ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <span className="h-5 w-5 rounded-full border border-border" />
                    )}
                    <span
                      className={
                        done
                          ? 'text-sm text-foreground'
                          : active
                            ? 'text-sm font-medium text-foreground'
                            : 'text-sm text-muted-foreground'
                      }
                    >
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            {error && (
              <div
                className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
                data-testid="processing-error"
              >
                <div className="font-semibold text-destructive">We couldn&apos;t finish the analysis.</div>
                <div className="mt-1 text-foreground/80">{friendlyErrorMessage(error)}</div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Customer details form */}
        <motion.div
          initial={PROGRESS_MOTION_INITIAL}
          animate={PROGRESS_MOTION_ANIMATE}
          transition={FORM_MOTION_TRANSITION}
          className="lg:col-span-5"
        >
          <Card className="rounded-2xl border-border/70 bg-card p-6 sm:p-8">
            <h2 className="font-display text-lg font-semibold text-foreground">
              Where should we send your report?
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              We’ll email your detailed analysis and follow up with a tailored proposal.
            </p>

            <form className="mt-6 space-y-4" onSubmit={submit} data-testid="customer-details-form">
              <div>
                <Label htmlFor="person" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                  <User className="h-4 w-4 text-muted-foreground" /> Your name
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="person"
                  value={personName}
                  onChange={(e) => setPersonName(e.target.value)}
                  placeholder="e.g., Priya Nair"
                  className="h-11 rounded-xl"
                  data-testid="customer-person-name-input"
                  disabled={submitted}
                  required
                />
              </div>
              <div>
                <Label htmlFor="company" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                  <Building2 className="h-4 w-4 text-muted-foreground" /> Company name
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g., Shree Cement Works"
                  className="h-11 rounded-xl"
                  data-testid="customer-company-name-input"
                  disabled={submitted}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                  <Mail className="h-4 w-4 text-muted-foreground" /> Email
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="h-11 rounded-xl"
                  data-testid="customer-email-input"
                  disabled={submitted}
                  required
                />
                {touched && email && !emailValid && (
                  <p className="mt-1 text-xs text-destructive">Please enter a valid email address.</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                  <Phone className="h-4 w-4 text-muted-foreground" /> Contact number{' '}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <div className="flex gap-2">
                  <Select value={country} onValueChange={changeCountry} disabled={submitted}>
                    <SelectTrigger
                      className="h-11 w-[7.5rem] shrink-0 rounded-xl"
                      aria-label="Country dialling code"
                      data-testid="customer-country-code-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.iso} value={c.iso}>
                          {c.dial} {c.iso}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={phone}
                    onChange={(e) => setPhone(toNationalDigits(e.target.value, selectedCountry))}
                    placeholder={'9'.repeat(selectedCountry.digits)}
                    // No maxLength: it makes the browser swallow the keystrokes
                    // past the limit, so a typed "+91 98765 43210" would stop at
                    // "9198765432" before the country code could be recognised.
                    // toNationalDigits does the capping instead.
                    className="h-11 flex-1 rounded-xl tabular"
                    data-testid="customer-contact-number-input"
                    disabled={submitted}
                  />
                </div>
                {touched && !phoneValid && (
                  <p className="mt-1 text-xs text-destructive">
                    Enter all {selectedCountry.digits} digits, or leave this blank.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                className="h-12 w-full rounded-xl text-base font-semibold transition-colors duration-200 hover:bg-[hsl(var(--brand-blue-2))]"
                data-testid="customer-details-submit-button"
              >
                {submitted ? 'Details saved' : busy ? 'Saving…' : 'Get full analysis'}
              </Button>
              <p className="text-xs text-muted-foreground">
                By submitting you agree to be contacted by UrjaPod Energy for a solar + BESS proposal.
              </p>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ProcessingState;
